/* ==========================================================================
   Allure – custom components
   - <allure-quick-add>: card add-to-cart icon, size panel, upsell rows.
     Adding to cart itself is handled by the theme's <product-form>, so the
     cart drawer updates and opens exactly like the native buttons.
   - <allure-reviews>: filters, load more, read more, photo lightbox.
   ========================================================================== */
(() => {
  if (window.AllureCustomLoaded) return;
  window.AllureCustomLoaded = true;

  const cartJsUrl = () => `${(window.routes && window.routes.cart_url) || '/cart'}.js`;

  /* Quick add ---------------------------------------------------------------- */
  const closeAllQuickAdds = except => {
    document.querySelectorAll('allure-quick-add.is-open').forEach(el => {
      if (el !== except) el.close();
    });
  };

  class AllureQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      this.isMounted = true;
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-aq-input]');
      this.toggleButton = this.querySelector('[data-aq-toggle]');
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('keydown', this.handleKeyDown);
    }

    handleClick = event => {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;

      // Keep clicks inside the component from reaching card links / carousels.
      event.stopPropagation();

      if (target.hasAttribute('data-aq-toggle')) {
        event.preventDefault();
        this.classList.contains('is-open') ? this.close() : this.open();
        return;
      }

      if (target.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      if (target.type === 'submit') {
        if (target.dataset.aqVariant && this.input) {
          this.input.value = target.dataset.aqVariant;
        }
        this.querySelectorAll('.is-pending').forEach(button => button.classList.remove('is-pending'));
        target.classList.add('is-pending');
        this.watchSubmit(target);
      }
    };

    handleChange = event => {
      const select = event.target.closest('[data-up-select]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const price = this.querySelector('.up__price-now');
      if (option && price && option.dataset.price) {
        price.textContent = option.dataset.price;
      }
    };

    handleKeyDown = event => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
        this.toggleButton?.focus();
      }
    };

    open() {
      closeAllQuickAdds(this);
      this.classList.add('is-open');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
      const firstChip = this.querySelector('.aq__chip:not([aria-disabled="true"])');
      firstChip?.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      this.toggleButton?.setAttribute('aria-expanded', 'false');
    }

    // The theme's <product-form> toggles the "loading" class on submit buttons
    // while the request runs. When it goes away, the request has finished.
    watchSubmit(button) {
      this.observer?.disconnect();
      clearTimeout(this.watchTimeout);
      let sawLoading = false;
      this.observer = new MutationObserver(() => {
        if (button.classList.contains('loading')) {
          sawLoading = true;
          return;
        }
        if (sawLoading) {
          this.observer.disconnect();
          this.handleSubmitEnd(button);
        }
      });
      this.observer.observe(button, { attributes: true, attributeFilter: ['class'] });
      this.watchTimeout = setTimeout(() => this.observer?.disconnect(), 15000);
    }

    async handleSubmitEnd(button) {
      button.classList.remove('is-pending');
      this.close();
      if (!this.isConnected || !this.form) return;

      // Only show the success state when the variant really is in the cart
      // (the theme shows its own notification on errors).
      const variantId = String(new FormData(this.form).get('id'));
      try {
        const cart = await fetch(cartJsUrl()).then(response => response.json());
        if (!cart.items.some(item => String(item.variant_id) === variantId)) return;
      } catch (error) {
        return;
      }

      this.classList.add('is-added');
      clearTimeout(this.addedTimeout);
      this.addedTimeout = setTimeout(() => this.classList.remove('is-added'), 1800);
    }
  }

  document.addEventListener('click', event => {
    if (!event.target.closest('allure-quick-add')) closeAllQuickAdds();
  });

  if (!customElements.get('allure-quick-add')) {
    customElements.define('allure-quick-add', AllureQuickAdd);
  }

  /* Reviews ------------------------------------------------------------------ */
  class AllureReviews extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      this.isMounted = true;
      this.perPage = parseInt(this.dataset.perPage, 10) || 6;
      this.shown = this.perPage;
      this.filter = 'all';
      this.cards = Array.from(this.querySelectorAll('[data-ar-card]'));
      this.track = this.querySelector('[data-ar-track]');
      this.loadButton = this.querySelector('[data-ar-load]');
      this.dotsBar = this.querySelector('[data-ar-dots-bar]');
      this.lightbox = this.querySelector('[data-ar-lightbox]');

      this.addEventListener('click', this.handleClick);
      this.track?.addEventListener('scroll', this.updateProgress, { passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        this.updateClamps();
        this.updateProgress();
      });
      this.resizeObserver.observe(this);

      this.apply();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    handleClick = event => {
      const filterButton = event.target.closest('[data-ar-filter]');
      if (filterButton) {
        const value = filterButton.dataset.arFilter;
        this.setFilter(value === this.filter && value !== 'all' ? 'all' : value);
        return;
      }

      if (event.target.closest('[data-ar-load]')) {
        this.shown += this.perPage;
        this.apply();
        return;
      }

      const expand = event.target.closest('[data-ar-expand]');
      if (expand) {
        expand.closest('[data-ar-card]')?.classList.add('is-expanded');
        expand.hidden = true;
        return;
      }

      const zoom = event.target.closest('[data-ar-zoom]');
      if (zoom && this.lightbox) {
        const media = this.lightbox.querySelector('[data-ar-lightbox-media]');
        const thumb = zoom.querySelector('img');
        const img = document.createElement('img');
        img.src = zoom.dataset.arZoom;
        img.alt = thumb ? thumb.alt : '';
        media.replaceChildren(img);
        this.lightbox.showModal();
        return;
      }

      if (event.target.closest('[data-ar-close]') || event.target === this.lightbox) {
        this.lightbox?.close();
      }
    };

    setFilter(value) {
      this.filter = value;
      this.shown = this.perPage;
      this.querySelectorAll('[data-ar-filter]').forEach(button => {
        const isActive = button.dataset.arFilter === value;
        button.classList.toggle('is-active', isActive);
        if (button.hasAttribute('aria-pressed')) {
          button.setAttribute('aria-pressed', String(isActive));
        }
      });
      this.apply();
      this.track?.scrollTo({ left: 0, behavior: 'smooth' });
    }

    matches(card) {
      if (this.filter === 'all') return true;
      if (this.filter === 'photos') return card.dataset.photo === 'true';
      return card.dataset.rating === this.filter;
    }

    apply() {
      let visible = 0;
      this.cards.forEach(card => {
        const isMatch = this.matches(card);
        card.classList.toggle('is-filtered-out', !isMatch);
        if (isMatch) {
          visible += 1;
          card.classList.toggle('is-paged-out', visible > this.shown);
        } else {
          card.classList.remove('is-paged-out');
        }
      });
      if (this.loadButton) this.loadButton.hidden = visible <= this.shown;
      requestAnimationFrame(() => {
        this.updateClamps();
        this.updateProgress();
      });
    }

    updateClamps() {
      this.cards.forEach(card => {
        const body = card.querySelector('[data-ar-body]');
        const button = card.querySelector('[data-ar-expand]');
        if (!body || !button || card.classList.contains('is-expanded')) return;
        button.hidden = body.scrollHeight <= body.clientHeight + 2;
      });
    }

    updateProgress = () => {
      if (!this.track || !this.dotsBar) return;
      const { scrollWidth, clientWidth, scrollLeft } = this.track;
      const ratio = scrollWidth > 0 ? Math.min(1, clientWidth / scrollWidth) : 1;
      const max = scrollWidth - clientWidth;
      const progress = max > 0 ? scrollLeft / max : 0;
      this.dotsBar.style.width = `${ratio * 100}%`;
      this.dotsBar.style.transform = `translateX(${progress * ((1 - ratio) / ratio) * 100}%)`;
    };

    reveal(card) {
      this.setFilter('all');
      this.shown = this.cards.length;
      this.apply();
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  if (!customElements.get('allure-reviews')) {
    customElements.define('allure-reviews', AllureReviews);
  }

  // Theme editor: make sure a selected review block is visible.
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest?.('[data-ar-card]');
    const host = card?.closest('allure-reviews');
    if (host && typeof host.reveal === 'function') host.reveal(card);
  });
})();

/* Estimated delivery dates ---------------------------------------------------
   <allure-delivery data-ship-min data-ship-max data-transit data-skip-days data-timezone data-locale>
   Business days are counted from "today" in the shop's timezone, so every visitor sees the same dates. */
(() => {
  if (customElements.get('allure-delivery')) return;

  const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

  class AllureDelivery extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      this.isMounted = true;
      this.render();
      this.timer = setInterval(() => this.render(), 60 * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    number(name, fallback) {
      const value = parseInt(this.dataset[name], 10);
      return Number.isNaN(value) ? fallback : value;
    }

    today() {
      try {
        const parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: this.dataset.timezone || 'Africa/Casablanca', year: 'numeric', month: 'numeric', day: 'numeric' })
          .formatToParts(new Date())
          .forEach(part => (parts[part.type] = Number(part.value)));
        return new Date(parts.year, parts.month - 1, parts.day);
      } catch (error) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
    }

    addBusinessDays(date, days) {
      const skip = (this.dataset.skipDays || '').split(',').filter(Boolean).map(Number);
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (!skip.includes(result.getDay())) added += 1;
      }
      return result;
    }

    format(date, withMonth) {
      const locale = this.dataset.locale || 'fr';
      const weekday = capitalize(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date));
      const day = date.getDate();
      const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
      return withMonth ? `${weekday} ${day} ${month}` : `${weekday} ${day}`;
    }

    range(from, to) {
      if (from.getTime() === to.getTime()) return this.format(from, true);
      if (from.getMonth() === to.getMonth()) return `${this.format(from, false)} – ${this.format(to, true)}`;
      return `${this.format(from, true)} – ${this.format(to, true)}`;
    }

    render() {
      const shipMin = this.number('shipMin', 1);
      const shipMax = Math.max(shipMin, this.number('shipMax', 2));
      const transit = this.number('transit', 2);
      const today = this.today();
      const shipped = this.querySelector('[data-adl-shipped]');
      const delivered = this.querySelector('[data-adl-delivered]');
      if (shipped) shipped.textContent = this.range(this.addBusinessDays(today, shipMin), this.addBusinessDays(today, shipMax));
      if (delivered) delivered.textContent = this.range(this.addBusinessDays(today, shipMin + transit), this.addBusinessDays(today, shipMax + transit));
    }
  }

  customElements.define('allure-delivery', AllureDelivery);
})();
